import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { allCategories } from "@/utils/videoUtils";

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-label transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-700 bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200/70">
      <div className="w-full px-4 md:px-6 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-900 text-background-50">
            <i className="ri-clapperboard-line text-lg"></i>
          </span>
          <span className="font-heading text-lg font-semibold text-foreground-950 whitespace-nowrap">
            日本伦理剧
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-2">
          <NavLink to="/" end className={navLinkClass}>
            首页
          </NavLink>
          <div
            className="relative"
            onMouseEnter={() => setCategoryOpen(true)}
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <NavLink to="/category/drama" className={navLinkClass}>
              <span className="flex items-center gap-1">
                分类 <i className="ri-arrow-down-s-line"></i>
              </span>
            </NavLink>
            {categoryOpen && (
              <div className="absolute left-0 top-full pt-1 w-52">
                        <div className="rounded-lg border border-background-200/70 bg-background-50 p-2 animate-soft-in">
                          {allCategories.map((cat) => (
                            <Link
                              key={cat.key}
                              to={`/category/${cat.key}`}
                              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-label text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                            >
                              <i className={`${cat.icon} text-base`}></i>
                              {cat.name}
                            </Link>
                          ))}
                        </div>
                      </div>
            )}
          </div>
          <NavLink to="/search" className={navLinkClass}>
            搜索
          </NavLink>
          <NavLink to="/about" className={navLinkClass}>
            关于
          </NavLink>
        </nav>

        <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-md ml-auto">
          <div className="relative w-full">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-base"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索剧名、导演、题材…"
              aria-label="搜索影视内容"
              className="w-full h-10 pl-9 pr-20 text-sm rounded-full border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
            <button
              type="submit"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-4 rounded-full text-sm font-label bg-accent-500 text-accent-950 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
        >
          <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 py-4 animate-soft-in">
          <form onSubmit={handleSearch} className="relative w-full mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-base"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索剧名、导演、题材…"
              aria-label="搜索影视内容"
              className="w-full h-11 pl-9 pr-20 text-sm rounded-full border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <button
              type="submit"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-9 px-4 rounded-full text-sm font-label bg-accent-500 text-accent-950 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <div className="flex flex-col gap-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 rounded-md text-sm font-label text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            {allCategories.map((cat) => (
              <Link
                key={cat.key}
                to={`/category/${cat.key}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-label text-foreground-800 hover:bg-background-100"
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
            <Link to="/about" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 rounded-md text-sm font-label text-foreground-800 hover:bg-background-100">
              关于
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}